import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Search, Lock, Send, MapPin } from "lucide-react";

export default function JobSearch() {
  const [f, setF] = useState({ level: "", season: "", state: "", role: "", gender: "", paid_only: false, housing_only: false, q: "" });
  const [results, setResults] = useState([]);
  const [unlocked, setUnlocked] = useState(true);
  const [busy, setBusy] = useState(false);
  const [interested, setInterested] = useState({});

  const search = async (filters = f) => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/jobs/search?${params.toString()}`);
      setResults(data.results || []);
      setUnlocked(data.unlocked);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };
  useEffect(() => { search(); }, []); // eslint-disable-line

  const express = async (jobId) => {
    try {
      await api.post("/coach/interests", { job_id: jobId, note: "" });
      setInterested((m) => ({ ...m, [jobId]: true }));
      toast.success("Interest sent — the program will see your profile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send interest");
    }
  };

  const inp = "bg-appcard border border-neutral-800 px-3 py-3 text-sm text-white outline-none focus:border-blaze";
  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-5xl">
        <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><Search size={14} /> JOB SEARCH</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">CLUB · HS · COLLEGE <span className="text-blaze">OPENINGS.</span></h1>

        {!unlocked && (
          <div className="mt-4 border border-blaze/50 bg-appcard p-4 flex flex-wrap items-center justify-between gap-3" data-testid="jobsearch-unlock-banner">
            <div className="text-sm text-neutral-300 flex items-center gap-2"><Lock size={14} className="text-blaze" /> Program names & full details are locked. Full details are free for every coach.</div>
            <Link to="/coach/jobs" data-testid="jobsearch-unlock-btn" className="bg-blaze text-white heading text-sm px-5 py-2.5 hover:glow-blaze transition-all">REFRESH</Link>
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="jobsearch-filters">
          <select data-testid="filter-level" className={inp} value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })}>
            <option value="">All levels</option><option value="Club">Club</option><option value="HS">High School</option><option value="College">College</option>
          </select>
          <select data-testid="filter-season" className={inp} value={f.season} onChange={(e) => setF({ ...f, season: e.target.value })}>
            <option value="">Any season</option><option value="Summer">Summer (club)</option><option value="Fall">Fall (club)</option>
          </select>
          <input data-testid="filter-state" className={inp} placeholder="State (e.g., CT)" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} />
          <select data-testid="filter-role" className={inp} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
            <option value="">Any role</option><option value="head_coach">Head Coach</option><option value="assistant_coach">Assistant</option>
          </select>
          <select data-testid="filter-gender" className={inp} value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}>
            <option value="">Any program</option><option value="Boys">Boys / Men</option><option value="Girls">Girls / Women</option>
          </select>
          <label className="flex items-center gap-2 text-xs text-neutral-400 px-2 cursor-pointer select-none">
            <input data-testid="filter-paid" type="checkbox" checked={f.paid_only} onChange={(e) => setF({ ...f, paid_only: e.target.checked })} className="accent-[rgb(var(--app-primary-rgb))] h-4 w-4" />
            Paid only
          </label>
          <label className="flex items-center gap-2 text-xs text-neutral-400 px-2 cursor-pointer select-none">
            <input data-testid="filter-housing" type="checkbox" checked={f.housing_only} onChange={(e) => setF({ ...f, housing_only: e.target.checked })} className="accent-[rgb(var(--app-primary-rgb))] h-4 w-4" />
            Housing provided
          </label>
        </div>
        <div className="mt-2 flex gap-2">
          <input data-testid="filter-q" className={`${inp} flex-1`} placeholder="Search programs, titles, keywords…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} />
          <button data-testid="jobsearch-go" onClick={() => search()} disabled={busy} className="bg-blaze text-white heading px-6 hover:glow-blaze transition-all disabled:opacity-50">
            {busy ? "…" : "SEARCH"}
          </button>
        </div>

        {/* Results */}
        <div className="mt-6 space-y-2" data-testid="jobsearch-results">
          <div className="text-xs text-neutral-500 tracking-widest">{results.length} OPENING{results.length === 1 ? "" : "S"}</div>
          {results.map((j) => (
            <div key={j.id} className="border border-neutral-800 bg-appcard p-4" data-testid={`jobsearch-result-${j.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-widest text-neutral-500">
                    {(j.division || j.level || "").toUpperCase()} · {(j.gender || "").toUpperCase()}
                    {j.club_meta?.season ? ` · ${j.club_meta.season.toUpperCase()} SEASON` : ""}
                  </div>
                  <div className="heading text-xl mt-1 flex items-center gap-2 flex-wrap">
                    {j.title}
                    {j.locked && <Lock size={13} className="text-blaze shrink-0" />}
                  </div>
                  <div className="text-sm text-neutral-300">{j.program_name}</div>
                  {j.location && <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {j.location}</div>}
                  <p className="text-xs text-neutral-400 mt-2 leading-relaxed max-w-2xl">{j.description}</p>
                  {j.club_meta?.pay_per_season && !j.locked && (
                    <div className="text-xs text-volt mt-1">{j.club_meta.pay_per_season}/season{j.club_meta.travel_perdiem ? ` · ${j.club_meta.travel_perdiem} per-diem` : ""}{j.club_meta.lodging_provided ? " · lodging provided" : ""}{j.club_meta.housing_provided ? " · season housing available" : ""}</div>
                  )}
                </div>
                <div className="shrink-0">
                  {j.locked ? (
                    <Link to={`/jobs/${j.id}`} className="inline-flex items-center gap-1.5 border border-blaze text-blaze text-xs tracking-widest px-3 py-2.5 hover:bg-blaze hover:text-white transition-colors">
                      <Lock size={12} /> UNLOCK
                    </Link>
                  ) : interested[j.id] ? (
                    <span className="text-xs tracking-widest text-volt">✓ INTEREST SENT</span>
                  ) : (
                    <button data-testid={`express-interest-${j.id}`} onClick={() => express(j.id)} className="inline-flex items-center gap-1.5 bg-blaze text-white text-xs tracking-widest px-4 py-2.5 hover:glow-blaze transition-all">
                      <Send size={12} /> EXPRESS INTEREST
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {!busy && results.length === 0 && <div className="text-neutral-500 text-sm py-8">No openings match those filters — try widening the search.</div>}
        </div>
      </div>
    </div>
  );
}
